<template>
  <div class="fixed inset-0 z-40 bg-black/30 flex items-center justify-center" @click.self="$emit('close')">
    <div class="bg-white rounded-lg border border-gray-200 w-[72rem] max-w-[95vw] max-h-[90vh] flex flex-col">
    <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
      <div>
        <h3 class="text-sm font-semibold text-gray-900">Model Import</h3>
        <p class="text-xs text-gray-500">Create Django records (Entities, InfoTags, Problems, Actions...) from a spreadsheet</p>
      </div>
      <button class="text-gray-400 hover:text-gray-600" @click="$emit('close')">×</button>
    </div>
    <div class="p-4 space-y-4 overflow-y-auto flex-1">
    <!-- Step 1: File Selection & Analysis -->
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <h3 class="text-sm font-semibold mb-3 text-gray-900">
        Step 1: Select File and Sheet
      </h3>

      <!-- Seeded from the Uploads browser: no upload step needed -->
      <div v-if="initialFile" class="mb-4 text-sm text-gray-700">
        <span class="text-xs font-medium text-gray-500 mr-2">File:</span>
        <span class="font-mono break-all">{{ initialFile.s3_key }}</span>
      </div>

      <!-- File Upload/Selection (reuse from Discovery) -->
      <div v-else class="mb-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Upload New File</h4>
        <div class="flex items-center gap-3">
          <input
            ref="fileInput"
            type="file"
            @change="onFileSelected"
            accept=".csv,.xlsx,.xls,.xlsm,.xlsb,.zip,.txt"
            class="block w-full text-sm text-gray-500
                   file:mr-4 file:py-2 file:px-4
                   file:rounded-md file:border-0
                   file:text-sm file:font-semibold
                   file:bg-blue-50 file:text-blue-700
                   hover:file:bg-blue-100"
          />
          <button
            @click="uploadFile"
            :disabled="!localFile || isUploading"
            class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
          >
            {{ isUploading ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>

      <!-- Analyze Button -->
      <button
        v-if="activeFile"
        @click="analyzeFile"
        :disabled="isAnalyzing"
        class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
      >
        {{ isAnalyzing ? 'Analyzing...' : 'Analyze File' }}
      </button>

      <!-- Sheet Selection -->
      <div v-if="fileAnalysis" class="mt-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Select Sheet</h4>
        <select
          v-model="selectedSheet"
          @change="onSheetSelected"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-dark"
        >
          <option :value="null">-- Select a sheet --</option>
          <template v-for="(fileItem, fileIdx) in fileAnalysis.file_sheets" :key="fileIdx">
            <option
              v-for="(sheet, sheetIdx) in fileItem.sheets"
              :key="`${fileIdx}-${sheetIdx}`"
              :value="JSON.stringify({ fileItem, sheet })"
            >
              {{ fileItem.file_name }} :: {{ sheet.name }}
            </option>
          </template>
        </select>
      </div>

      <!-- Classify sheet to get columns -->
      <div v-if="selectedSheet && !sheetClassification" class="mt-4">
        <button
          @click="classifySheet"
          :disabled="isClassifying"
          class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
        >
          {{ isClassifying ? 'Analyzing Sheet...' : 'Analyze Sheet Columns' }}
        </button>
      </div>

      <!-- Show classification results -->
      <div v-if="sheetClassification" class="mt-4 p-3 bg-green-50 border border-green-200 rounded">
        <p class="text-sm text-green-800">
          ✓ Sheet analyzed: {{ sheetClassification.columns.length }} columns found
        </p>
      </div>
    </div>

    <!-- Step 2: Select Target Model -->
    <div v-if="selectedSheet && sheetClassification" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <h3 class="text-sm font-semibold mb-3 text-gray-900">
        Step 2: Select Target Model
      </h3>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Model</label>
          <select
            v-model="selectedModel"
            @change="onModelSelected"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-dark"
          >
            <option :value="null">-- Select a model --</option>
            <option
              v-for="model in availableModels"
              :key="model.full_name"
              :value="model"
            >
              {{ model.label }}
            </option>
          </select>
          <p v-if="availableModels.length === 0" class="text-xs text-red-600 mt-1">
            No models available. Check backend configuration.
          </p>
          <p v-else class="text-xs text-gray-500 mt-1">
            {{ availableModels.length }} models available
          </p>
        </div>
      </div>

      <!-- Show model fields -->
      <div v-if="selectedModel && modelFields" class="mt-4">
        <p class="text-sm text-gray-600">
          Model has {{ modelFields.filter(f => !f.system_field).length }} fields
          ({{ modelFields.filter(f => f.required).length }} required,
          {{ modelFields.filter(f => f.is_foreign_key).length }} foreign keys)
        </p>
      </div>
    </div>

    <!-- Step 3: InfoTag Configuration (if InfoTag selected) -->
    <InfoTagImportConfig
      v-if="isInfoTagImport && selectedSheet && sheetClassification"
      :config="infotagConfig"
      :infotag-types="infotagTypes"
      :sheet-columns="sheetColumns"
      :identifier-types="identifierTypes"
      @update:config="infotagConfig = $event"
    />

    <!-- Preview Button for InfoTag -->
    <div v-if="isInfoTagImport && selectedSheet && sheetClassification" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <button
        @click="previewImport"
        :disabled="!canPreview || isPreviewing"
        class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
      >
        {{ isPreviewing ? 'Validating...' : 'Preview & Validate' }}
      </button>
    </div>

    <!-- Step 3: Configure Entity Import -->
    <EntityImportConfig
      v-if="isEntityImport && selectedSheet && sheetClassification"
      :config="entityConfig"
      :entity-types="entityTypes"
      :entity-attributes="entityAttributes"
      :sheet-columns="sheetColumns"
      :identifier-types="identifierTypes"
      @update:config="entityConfig = $event"
      @load-attributes="loadEntityAttributes"
    />

    <!-- Preview Button for Entity -->
    <div v-if="isEntityImport && selectedSheet && sheetClassification" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <button
        @click="previewImport"
        :disabled="!canPreview || isPreviewing"
        class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
      >
        {{ isPreviewing ? 'Validating...' : 'Preview & Validate' }}
      </button>
    </div>

    <!-- Step 3: Map Columns to Fields (Regular Import) -->
    <RegularImportConfig
      v-if="!isInfoTagImport && !isEntityImport && selectedModel && modelFields"
      :regular-fields="regularFields"
      :foreign-key-fields="foreignKeyFields"
      :sheet-columns="sheetColumns"
      :identifier-types="identifierTypes"
      :field-mappings="fieldMappings"
      :foreign-key-mappings="foreignKeyMappings"
      :related-model-fields-cache="relatedModelFieldsCache"
      @update:field-mappings="fieldMappings = $event"
      @update:foreign-key-mappings="foreignKeyMappings = $event"
      @load-related-fields="loadRelatedModelFields"
    />

    <!-- Preview Button for Regular Import -->
    <div v-if="!isInfoTagImport && !isEntityImport && selectedModel && modelFields" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <button
        @click="previewImport"
        :disabled="!canPreview || isPreviewing"
        class="px-3 py-1.5 text-sm bg-primary-500 text-white rounded hover:bg-primary-600 disabled:bg-gray-400 transition-colors"
      >
        {{ isPreviewing ? 'Validating...' : 'Preview & Validate' }}
      </button>
    </div>

    <!-- Step 4: Validation Results & Preview -->
    <div v-if="previewResults" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <h3 class="text-sm font-semibold mb-3 text-gray-900">
        Step 4: Validation Results
      </h3>

      <!-- Summary Stats -->
      <div class="grid grid-cols-4 gap-4 mb-6">
        <div class="bg-blue-50 p-3 rounded">
          <p class="text-xs text-blue-600 uppercase">Total Rows</p>
          <p class="text-xl font-semibold text-blue-900">{{ previewResults.total_rows }}</p>
        </div>
        <div
          :class="previewResults.can_proceed ? 'bg-green-50' : 'bg-red-50'"
          class="p-3 rounded"
        >
          <p :class="previewResults.can_proceed ? 'text-green-600' : 'text-red-600'" class="text-xs uppercase">
            Status
          </p>
          <p
            :class="previewResults.can_proceed ? 'text-green-900' : 'text-red-900'"
            class="text-xl font-semibold"
          >
            {{ previewResults.can_proceed ? 'Ready' : 'Issues' }}
          </p>
        </div>
        <div class="bg-yellow-50 p-3 rounded">
          <p class="text-xs text-yellow-600 uppercase">Validation Errors</p>
          <p class="text-xl font-semibold text-yellow-900">
            {{ previewResults.validation_results.total_errors }}
          </p>
        </div>
        <div class="bg-purple-50 p-3 rounded">
          <p class="text-xs text-purple-600 uppercase">FK Unresolved</p>
          <p class="text-xl font-semibold text-purple-900">
            {{ getTotalUnresolvedFKs() }}
          </p>
        </div>
      </div>

      <!-- InfoTag Validation Details -->
      <div
        v-if="previewResults.validation_results.infotag_validation"
        class="mb-6"
      >
        <h4 class="text-md font-semibold text-gray-800 mb-3">InfoTag Import Validation</h4>

        <!-- Entity Validation -->
        <div class="p-3 rounded border mb-3"
          :class="previewResults.validation_results.infotag_validation.entity_validation.not_found === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <div class="flex justify-between items-center">
            <div>
              <span class="font-medium">Entity Identifier Matching</span>
              <span class="text-xs text-gray-600 ml-2">
                → {{ previewResults.validation_results.infotag_validation.entity_validation.use_fuzzy_matching ?
                     `Fuzzy (${previewResults.validation_results.infotag_validation.entity_validation.fuzzy_threshold}%)` :
                     'Exact' }}
              </span>
            </div>
            <div class="text-sm">
              <span class="text-green-600 font-medium">
                {{ previewResults.validation_results.infotag_validation.entity_validation.found_exact }} exact
              </span>
              <span v-if="previewResults.validation_results.infotag_validation.entity_validation.found_fuzzy > 0"
                class="text-purple-600 font-medium ml-2">
                {{ previewResults.validation_results.infotag_validation.entity_validation.found_fuzzy }} fuzzy
              </span>
              <span v-if="previewResults.validation_results.infotag_validation.entity_validation.not_found > 0"
                class="text-red-600 font-medium ml-3">
                {{ previewResults.validation_results.infotag_validation.entity_validation.not_found }} NOT FOUND
              </span>
            </div>
          </div>

          <!-- Entity Fuzzy Matches -->
          <div v-if="previewResults.validation_results.infotag_validation.entity_fuzzy_matches &&
                     previewResults.validation_results.infotag_validation.entity_fuzzy_matches.length > 0"
               class="mt-3">
            <p class="text-xs text-purple-700 font-medium mb-2">
              Entity Fuzzy Matches ({{ previewResults.validation_results.infotag_validation.entity_fuzzy_matches.length }} matches):
            </p>
            <div class="space-y-2 max-h-96 overflow-y-auto">
              <div v-for="(fuzzyMatch, idx) in previewResults.validation_results.infotag_validation.entity_fuzzy_matches"
                   :key="idx"
                   class="bg-white p-2 rounded border border-purple-200">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1">
                    <p class="text-xs font-medium text-gray-800">
                      <span class="text-purple-600">{{ fuzzyMatch.sheet_value }}</span>
                      <span class="text-gray-500 mx-1">→</span>
                      <span class="text-green-600">{{ fuzzyMatch.matched_value }}</span>
                    </p>
                    <div v-if="fuzzyMatch.all_candidates && fuzzyMatch.all_candidates.length > 1" class="mt-1">
                      <p class="text-xs text-gray-500">Other candidates:</p>
                      <div class="ml-2 space-y-1">
                        <div v-for="(candidate, cIdx) in fuzzyMatch.all_candidates.slice(1)"
                             :key="cIdx"
                             class="flex items-center gap-2">
                          <span class="px-1 py-0.5 text-xs rounded" :class="getScoreClass(candidate.score)">
                            {{ candidate.score }}%
                          </span>
                          <span class="text-xs text-gray-600">{{ candidate.value }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span class="px-2 py-0.5 text-xs rounded shrink-0" :class="getScoreClass(fuzzyMatch.score)">
                    {{ fuzzyMatch.score }}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Unresolved Entity Identifiers -->
          <div v-if="previewResults.validation_results.infotag_validation.entity_validation.unresolved_identifiers &&
                     previewResults.validation_results.infotag_validation.entity_validation.unresolved_identifiers.length > 0"
               class="mt-3">
            <p class="text-xs text-red-600 font-medium mb-2">
              Unresolved Entity Identifiers ({{ previewResults.validation_results.infotag_validation.entity_validation.unresolved_identifiers.length }}):
            </p>
            <div class="max-h-60 overflow-y-auto space-y-1 bg-white p-2 rounded border border-red-200">
              <div v-for="(identifier, idx) in previewResults.validation_results.infotag_validation.entity_validation.unresolved_identifiers"
                   :key="idx"
                   class="text-xs text-red-800 py-1 px-2 bg-red-50 rounded">
                {{ identifier }}
              </div>
            </div>
          </div>
        </div>

        <!-- InfoTag Matches Summary -->
        <div v-if="previewResults.validation_results.infotag_validation.infotag_matches"
             class="p-3 rounded border bg-blue-50 border-blue-200 mb-3">
          <p class="text-sm font-medium text-gray-800 mb-2">InfoTag Names ({{ previewResults.validation_results.infotag_validation.infotag_matches.total }} unique):</p>
          <div class="grid grid-cols-3 gap-2 text-xs">
            <div>
              <span class="text-green-600 font-medium">{{ previewResults.validation_results.infotag_validation.infotag_matches.exact }}</span> Exact matches
            </div>
            <div>
              <span class="text-purple-600 font-medium">{{ previewResults.validation_results.infotag_validation.infotag_matches.fuzzy }}</span> Fuzzy matches
            </div>
            <div>
              <span class="text-blue-600 font-medium">{{ previewResults.validation_results.infotag_validation.infotag_matches.new }}</span> Will be created
            </div>
          </div>
        </div>

        <!-- InfoTag Fuzzy Matches Detail -->
        <div v-if="previewResults.validation_results.infotag_validation.infotag_fuzzy_matches &&
                   previewResults.validation_results.infotag_validation.infotag_fuzzy_matches.length > 0"
             class="p-3 rounded border bg-purple-50 border-purple-200">
          <p class="text-sm font-medium text-gray-800 mb-2">
            InfoTag Fuzzy Matches ({{ previewResults.validation_results.infotag_validation.infotag_fuzzy_matches.length }}):
          </p>
          <div class="space-y-2 max-h-96 overflow-y-auto">
            <div v-for="(fuzzyMatch, idx) in previewResults.validation_results.infotag_validation.infotag_fuzzy_matches"
                 :key="idx"
                 class="bg-white p-2 rounded border border-purple-300">
              <div class="flex items-start justify-between gap-2">
                <div class="flex-1">
                  <p class="text-xs font-medium text-gray-800">
                    <span class="text-purple-600">{{ fuzzyMatch.sheet_value }}</span>
                    <span class="text-gray-500 mx-1">→</span>
                    <span class="text-green-600">{{ fuzzyMatch.matched_value }}</span>
                  </p>
                  <div v-if="fuzzyMatch.all_candidates && fuzzyMatch.all_candidates.length > 1" class="mt-1">
                    <p class="text-xs text-gray-500">Other candidates:</p>
                    <div class="ml-2 space-y-1">
                      <div v-for="(candidate, cIdx) in fuzzyMatch.all_candidates.slice(1)"
                           :key="cIdx"
                           class="flex items-center gap-2">
                        <span class="px-1 py-0.5 text-xs rounded" :class="getScoreClass(candidate.score)">
                          {{ candidate.score }}%
                        </span>
                        <span class="text-xs text-gray-600">{{ candidate.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <span class="px-2 py-0.5 text-xs rounded shrink-0" :class="getScoreClass(fuzzyMatch.score)">
                  {{ fuzzyMatch.score }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Entity Validation Details -->
      <div
        v-if="previewResults.validation_results.entity_validation"
        class="mb-6"
      >
        <h4 class="text-md font-semibold text-gray-800 mb-3">Entity Import Validation</h4>

        <!-- Parent Entity Validation -->
        <div class="p-3 rounded border mb-3"
          :class="previewResults.validation_results.entity_validation.parent_validation.not_found === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <div class="flex justify-between items-center">
            <div>
              <span class="font-medium">Parent Entity Matching</span>
              <span class="text-xs text-gray-600 ml-2">
                → {{ previewResults.validation_results.entity_validation.parent_validation.use_fuzzy_matching ?
                     `Fuzzy (${previewResults.validation_results.entity_validation.parent_validation.fuzzy_threshold}%)` :
                     'Exact' }}
              </span>
            </div>
            <div class="text-sm">
              <span class="text-green-600 font-medium">
                {{ previewResults.validation_results.entity_validation.parent_validation.found_exact }} exact
              </span>
              <span v-if="previewResults.validation_results.entity_validation.parent_validation.found_fuzzy > 0"
                class="text-purple-600 font-medium ml-2">
                {{ previewResults.validation_results.entity_validation.parent_validation.found_fuzzy }} fuzzy
              </span>
              <span v-if="previewResults.validation_results.entity_validation.parent_validation.not_found > 0"
                class="text-red-600 font-medium ml-3">
                {{ previewResults.validation_results.entity_validation.parent_validation.not_found }} NOT FOUND
              </span>
            </div>
          </div>

          <!-- Parent Fuzzy Matches -->
          <div v-if="previewResults.validation_results.entity_validation.parent_fuzzy_matches &&
                     previewResults.validation_results.entity_validation.parent_fuzzy_matches.length > 0"
               class="mt-3">
            <p class="text-xs text-purple-700 font-medium mb-2">
              Parent Fuzzy Matches ({{ previewResults.validation_results.entity_validation.parent_fuzzy_matches.length }} matches):
            </p>
            <div class="space-y-2 max-h-96 overflow-y-auto">
              <div v-for="(fuzzyMatch, idx) in previewResults.validation_results.entity_validation.parent_fuzzy_matches"
                   :key="idx"
                   class="bg-white p-2 rounded border border-purple-200">
                <div class="flex items-start justify-between gap-2">
                  <div class="flex-1">
                    <p class="text-xs font-medium text-gray-800">
                      <span class="text-purple-600">{{ fuzzyMatch.sheet_value }}</span>
                      <span class="text-gray-500 mx-1">→</span>
                      <span class="text-green-600">{{ fuzzyMatch.matched_value }}</span>
                    </p>
                    <div v-if="fuzzyMatch.all_candidates && fuzzyMatch.all_candidates.length > 1" class="mt-1">
                      <p class="text-xs text-gray-500">Other candidates:</p>
                      <div class="ml-2 space-y-1">
                        <div v-for="(candidate, cIdx) in fuzzyMatch.all_candidates.slice(1)"
                             :key="cIdx"
                             class="flex items-center gap-2">
                          <span class="px-1 py-0.5 text-xs rounded" :class="getScoreClass(candidate.score)">
                            {{ candidate.score }}%
                          </span>
                          <span class="text-xs text-gray-600">{{ candidate.value }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span class="px-2 py-0.5 text-xs rounded shrink-0" :class="getScoreClass(fuzzyMatch.score)">
                    {{ fuzzyMatch.score }}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Unresolved Parent Identifiers -->
          <div v-if="previewResults.validation_results.entity_validation.parent_validation.unresolved_identifiers &&
                     previewResults.validation_results.entity_validation.parent_validation.unresolved_identifiers.length > 0"
               class="mt-3">
            <p class="text-xs text-red-600 font-medium mb-2">
              Unresolved Parent Identifiers ({{ previewResults.validation_results.entity_validation.parent_validation.unresolved_identifiers.length }}):
            </p>
            <div class="max-h-60 overflow-y-auto space-y-1 bg-white p-2 rounded border border-red-200">
              <div v-for="(identifier, idx) in previewResults.validation_results.entity_validation.parent_validation.unresolved_identifiers"
                   :key="idx"
                   class="text-xs text-red-800 py-1 px-2 bg-red-50 rounded">
                {{ identifier }}
              </div>
            </div>
          </div>
        </div>

        <!-- Entity Name Validation -->
        <div v-if="previewResults.validation_results.entity_validation.entity_name_validation"
             class="p-3 rounded border mb-3"
             :class="previewResults.validation_results.entity_validation.entity_name_validation.missing === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <div class="flex justify-between items-center">
            <span class="font-medium">Entity Name Validation</span>
            <div class="text-sm">
              <span class="text-green-600 font-medium">
                {{ previewResults.validation_results.entity_validation.entity_name_validation.present }} present
              </span>
              <span v-if="previewResults.validation_results.entity_validation.entity_name_validation.missing > 0"
                class="text-red-600 font-medium ml-3">
                {{ previewResults.validation_results.entity_validation.entity_name_validation.missing }} MISSING
              </span>
            </div>
          </div>
        </div>

        <!-- Entity Identifier Validation -->
        <div v-if="previewResults.validation_results.entity_validation.entity_identifier_validation"
             class="p-3 rounded border mb-3"
             :class="previewResults.validation_results.entity_validation.entity_identifier_validation.missing === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <div class="flex justify-between items-center">
            <span class="font-medium">Entity Identifier Validation</span>
            <div class="text-sm">
              <span class="text-green-600 font-medium">
                {{ previewResults.validation_results.entity_validation.entity_identifier_validation.present }} present
              </span>
              <span v-if="previewResults.validation_results.entity_validation.entity_identifier_validation.missing > 0"
                class="text-red-600 font-medium ml-3">
                {{ previewResults.validation_results.entity_validation.entity_identifier_validation.missing }} MISSING
              </span>
            </div>
          </div>
        </div>

        <!-- Attribute Validation -->
        <div v-if="previewResults.validation_results.entity_validation.attribute_validation &&
                   previewResults.validation_results.entity_validation.attribute_validation.issues &&
                   previewResults.validation_results.entity_validation.attribute_validation.issues.length > 0"
             class="p-3 rounded border bg-red-50 border-red-200">
          <p class="font-medium text-gray-800 mb-2">Attribute Validation Issues:</p>
          <div class="space-y-2">
            <div v-for="(issue, idx) in previewResults.validation_results.entity_validation.attribute_validation.issues"
                 :key="idx"
                 class="bg-white p-2 rounded border border-red-300">
              <p class="text-xs font-medium text-gray-800">
                <span class="text-red-600">{{ issue.attribute_name }}</span> (required)
              </p>
              <p class="text-xs text-gray-600 mt-1">
                Column: <span class="font-mono">{{ issue.column }}</span> -
                <span class="text-red-600 font-medium">{{ issue.missing_count }} rows missing</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Foreign Key Validation Details -->
      <div
        v-if="Object.keys(previewResults.validation_results.foreign_key_validation).length > 0"
        class="mb-6"
      >
        <h4 class="text-md font-semibold text-gray-800 mb-3">Foreign Key Resolution</h4>
        <div class="space-y-2">
          <div
            v-for="(fkVal, fieldName) in previewResults.validation_results.foreign_key_validation"
            :key="fieldName"
            class="p-3 rounded border"
            :class="getFKValidationClass(fkVal)"
          >
            <div class="flex justify-between items-center">
              <div>
                <span class="font-medium">{{ fieldName }}</span>
                <span class="text-xs text-gray-600 ml-2">→ {{ fkVal.related_model }}</span>
                <span v-if="fkVal.lookup_type === 'entity_identifier'" class="text-xs text-blue-600 ml-1">
                  (via {{ fkVal.identifier_type }})
                </span>
                <span v-else-if="fkVal.lookup_type === 'direct_field_match'" class="text-xs text-purple-600 ml-1">
                  (match on {{ fkVal.model_field }})
                </span>
                <span v-if="fkVal.use_fuzzy_matching" class="text-xs text-purple-600 ml-1">
                  [Fuzzy: {{ fkVal.fuzzy_threshold }}%]
                </span>
              </div>
              <div class="text-sm">
                <span class="text-green-600 font-medium">{{ fkVal.found }} exact</span>
                <span v-if="fkVal.fuzzy_match_count > 0" class="text-purple-600 font-medium ml-2">
                  {{ fkVal.fuzzy_match_count }} fuzzy
                </span>
                <span v-if="fkVal.not_found > 0" class="text-red-600 font-medium ml-3">
                  {{ fkVal.not_found }} NOT FOUND
                </span>
              </div>
            </div>

            <!-- Show fuzzy matches -->
            <div v-if="fkVal.fuzzy_matches && fkVal.fuzzy_matches.length > 0" class="mt-3">
              <p class="text-xs text-purple-700 font-medium mb-2">Fuzzy Match Candidates:</p>
              <div class="space-y-2">
                <div
                  v-for="(fuzzyMatch, idx) in fkVal.fuzzy_matches"
                  :key="idx"
                  class="bg-white p-2 rounded border border-purple-200"
                >
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <p class="text-xs font-medium text-gray-800">
                        Sheet Value: <span class="text-purple-600">{{ fuzzyMatch.sheet_value }}</span>
                      </p>
                      <div class="mt-1 space-y-1">
                        <p class="text-xs text-gray-600">Suggested Matches:</p>
                        <div class="ml-3 space-y-1">
                          <div
                            v-for="(candidate, cIdx) in fuzzyMatch.candidates"
                            :key="cIdx"
                            class="flex items-center gap-2"
                          >
                            <span
                              class="px-2 py-0.5 text-xs rounded"
                              :class="getScoreClass(candidate.score)"
                            >
                              {{ candidate.score }}%
                            </span>
                            <span class="text-xs text-gray-700">{{ candidate.value }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Show unresolved identifiers -->
            <div v-if="fkVal.unresolved_identifiers && fkVal.unresolved_identifiers.length > 0" class="mt-3">
              <p class="text-xs text-red-600 font-medium mb-2">
                Unresolved values ({{ fkVal.unresolved_identifiers.length }}) - no matches found:
              </p>
              <div class="max-h-60 overflow-y-auto space-y-1 bg-white p-2 rounded border border-red-200">
                <div v-for="(identifier, idx) in fkVal.unresolved_identifiers"
                     :key="idx"
                     class="text-xs text-red-800 py-1 px-2 bg-red-50 rounded">
                  {{ identifier }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button
          v-if="!previewResults.can_proceed"
          @click="showSkipInvalidOption = !showSkipInvalidOption"
          class="px-3 py-1.5 text-sm rounded border border-gray-200 text-gray-600 hover:border-gray-400 transition-colors"
        >
          {{ showSkipInvalidOption ? 'Hide Options' : 'Show Options' }}
        </button>

        <button
          v-if="previewResults.can_proceed || showSkipInvalidOption"
          @click="onExecuteClick"
          :disabled="isExecuting"
          class="px-3 py-1.5 text-sm rounded text-white transition-colors disabled:bg-gray-400"
          :class="executeArmed ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-500 hover:bg-primary-600'"
        >
          <template v-if="isExecuting">Creating...</template>
          <template v-else-if="executeArmed">
            Click again to create {{ previewResults.total_rows }} rows — this writes to the database
          </template>
          <template v-else>{{ showSkipInvalidOption ? 'Create Valid Rows Only' : 'Create All' }}</template>
        </button>
      </div>

      <div v-if="showSkipInvalidOption && !previewResults.can_proceed" class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
        <p class="text-sm text-yellow-800">
          ⚠️ This will skip rows with validation errors and only create valid rows.
          Invalid rows will be reported but not created.
        </p>
      </div>
    </div>

    <!-- Step 5: Results -->
    <div v-if="executionResults" class="bg-white rounded-lg border border-gray-200 p-4 mb-6">
      <h3 class="text-sm font-semibold mb-3 text-gray-900">
        Import Complete
      </h3>

      <div class="grid grid-cols-3 gap-4 mb-6">
        <div class="bg-green-50 p-4 rounded">
          <p class="text-sm text-green-600 uppercase">Created</p>
          <p class="text-xl font-semibold text-green-900">{{ executionResults.rows_created }}</p>
        </div>
        <div class="bg-red-50 p-4 rounded">
          <p class="text-sm text-red-600 uppercase">Failed</p>
          <p class="text-xl font-semibold text-red-900">{{ executionResults.rows_failed }}</p>
        </div>
        <div class="bg-blue-50 p-4 rounded">
          <p class="text-sm text-blue-600 uppercase">Duration</p>
          <p class="text-xl font-semibold text-blue-900">{{ executionResults.duration_seconds.toFixed(2) }}s</p>
        </div>
      </div>

      <p class="text-green-700 font-medium">{{ executionResults.message }}</p>

      <!-- Show errors if any -->
      <div v-if="executionResults.errors && executionResults.errors.length > 0" class="mt-4">
        <h4 class="text-md font-semibold text-red-800 mb-2">Errors ({{ executionResults.errors.length }})</h4>
        <div class="bg-red-50 border border-red-200 rounded p-3 max-h-60 overflow-y-auto">
          <ul class="text-sm text-red-800 space-y-1">
            <li v-for="(error, idx) in executionResults.errors" :key="idx">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <button
        @click="resetWorkflow"
        class="btn-primary mt-6"
      >
        Start New Import
      </button>
    </div>

    <!-- Error Display -->
    <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <p class="text-sm text-red-800">{{ errorMessage }}</p>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InfoTagImportConfig from './InfoTagImportConfig.vue';
import EntityImportConfig from './EntityImportConfig.vue';
import RegularImportConfig from './RegularImportConfig.vue';

export default {
  name: 'ModelImportModal',

  components: {
    InfoTagImportConfig,
    EntityImportConfig,
    RegularImportConfig
  },

  props: {
    // Seed with a file already in S3 (from the Uploads browser): skips the
    // upload step and analyzes immediately.
    initialFile: { type: Object, default: null },
  },

  emits: ['close'],

  data() {
    return {
      // File management
      localFile: null,
      activeFile: null,
      isUploading: false,
      isAnalyzing: false,

      // File analysis
      fileAnalysis: null,
      selectedSheet: null,
      sheetColumns: [],
      sheetClassification: null,
      isClassifying: false,

      // Model selection
      availableModels: [],
      selectedModel: null,
      modelFields: null,
      identifierTypes: [],

      // InfoTag-specific
      infotagTypes: [],
      infotagConfig: {
        infotag_type_id: null,
        infotag_name_column: null,
        entity_identifier_column: null,
        entity_identifier_type: null,
        use_entity_fuzzy_matching: false,
        entity_fuzzy_threshold: 80,
        use_fuzzy_matching: false,
        fuzzy_threshold: 80,
        overwrite: false
      },

      // Entity-specific
      entityTypes: [],
      entityAttributes: [],
      entityConfig: {
        entity_type_id: null,
        entity_name_column: null,
        entity_identifier_column: null,
        entity_identifier_type: null,
        parent_identifier_column: null,
        parent_identifier_type: null,
        use_parent_fuzzy_matching: false,
        parent_fuzzy_threshold: 80,
        attribute_mappings: {}
      },

      // Field mappings
      fieldMappings: {},
      foreignKeyMappings: {},
      relatedModelFieldsCache: {},  // Cache related model fields by field name

      // Preview & validation
      isPreviewing: false,
      previewResults: null,
      showSkipInvalidOption: false,

      // Execution
      isExecuting: false,
      executeArmed: false,
      executionResults: null,
      pollCancelled: false,

      // Error handling
      errorMessage: null,
    };
  },

  computed: {
    regularFields() {
      if (!this.modelFields) return [];
      return this.modelFields.filter(f => !f.is_foreign_key && !f.system_field);
    },

    foreignKeyFields() {
      if (!this.modelFields) return [];
      return this.modelFields.filter(f => f.is_foreign_key && !f.system_field);
    },

    isInfoTagImport() {
      return this.selectedModel && this.selectedModel.model === 'InfoTag';
    },

    isEntityImport() {
      return this.selectedModel && this.selectedModel.model === 'Entity';
    },

    canPreview() {
      // isMapped treats '' the same as null: the placeholder <option> is bound
      // :value="null" but the change handlers read back the raw DOM string, so
      // a re-cleared selection round-trips as ''.
      const isMapped = (v) => v !== null && v !== undefined && v !== '';

      if (this.isEntityImport) {
        return isMapped(this.entityConfig.entity_type_id) &&
               isMapped(this.entityConfig.entity_name_column) &&
               isMapped(this.entityConfig.entity_identifier_column) &&
               isMapped(this.entityConfig.entity_identifier_type) &&
               isMapped(this.entityConfig.parent_identifier_column) &&
               isMapped(this.entityConfig.parent_identifier_type) &&
               this.selectedSheet !== null;
      } else if (this.isInfoTagImport) {
        return isMapped(this.infotagConfig.infotag_type_id) &&
               isMapped(this.infotagConfig.infotag_name_column) &&
               isMapped(this.infotagConfig.entity_identifier_column) &&
               isMapped(this.infotagConfig.entity_identifier_type);
      }

      // Regular imports. FK mapping objects carry sheet_column plus either
      // identifier_type (entity lookup) or model_field (direct match) — the
      // old check tested fields that don't exist on these objects, so Preview
      // enabled with nothing mapped.
      const hasFieldMapping = Object.values(this.fieldMappings).some(isMapped);
      const hasFKMapping = Object.values(this.foreignKeyMappings).some((fk) => {
        if (fk.lookup_type === 'entity_identifier') {
          return isMapped(fk.sheet_column) && isMapped(fk.identifier_type);
        }
        return isMapped(fk.sheet_column) && isMapped(fk.model_field);
      });
      return hasFieldMapping || hasFKMapping;
    },
  },

  async mounted() {
    if (this.initialFile) {
      this.activeFile = {
        key: this.initialFile.s3_key,
        s3_key: this.initialFile.s3_key,
        filename: this.initialFile.filename,
      };
      this.analyzeFile();
    }
    // Independent lookups — load in parallel, and collect every failure
    // instead of letting the last error overwrite the rest.
    const failures = [];
    await Promise.all([
      this.loadAvailableModels().catch(() => failures.push('models')),
      this.loadIdentifierTypes().catch(() => failures.push('identifier types')),
      this.loadInfoTagTypes().catch(() => failures.push('InfoTag types')),
      this.loadEntityTypes().catch(() => failures.push('entity types')),
    ]);
    if (failures.length) {
      this.errorMessage = `Failed to load: ${failures.join(', ')}. Superuser access is required for Model Import.`;
    }
  },
  beforeUnmount() {
    this.pollCancelled = true;
  },

  methods: {
    // File handling
    onFileSelected(event) {
      this.localFile = event.target.files[0];
      this.clearError();
    },

    async uploadFile() {
      if (!this.localFile) return;

      this.isUploading = true;
      this.clearError();

      try {
        // Step 1: Get presigned upload URL from backend
        const presignResponse = await axios.post('/api/etl_module/upload/', {
          filename: this.localFile.name,
          content_type: this.localFile.type || 'application/octet-stream',
        }, {
          headers: { 'Content-Type': 'application/json' },
        });

        const { upload_url, s3_key, filename } = presignResponse.data;

        // Step 2: Upload file directly to S3 using presigned URL.
        // fetch does NOT reject on 4xx/5xx — an unchecked response here used
        // to show "uploaded" for a file that never reached S3.
        const putResponse = await fetch(upload_url, {
          method: 'PUT',
          body: this.localFile,
          headers: {
            'Content-Type': this.localFile.type || 'application/octet-stream',
          },
        });
        if (!putResponse.ok) {
          throw new Error(`S3 upload failed (${putResponse.status})`);
        }

        this.activeFile = {
          key: s3_key,
          filename: filename,
          s3_key: s3_key
        };
      } catch (error) {
        this.errorMessage = `Upload failed: ${error.response?.data?.error || error.message}`;
      } finally {
        this.isUploading = false;
      }
    },

    async analyzeFile() {
      if (!this.activeFile) return;

      this.isAnalyzing = true;
      this.clearError();

      try {
        const response = await axios.post('/api/etl_module/analyze/', {
          s3_key: this.activeFile.s3_key
        });
        this.fileAnalysis = response.data;
      } catch (error) {
        this.errorMessage = `Analysis failed: ${error.response?.data?.error || error.message}`;
      } finally {
        this.isAnalyzing = false;
      }
    },

    // Sheet selection
    onSheetSelected() {
      if (this.selectedSheet && typeof this.selectedSheet === 'string') {
        try {
          const parsed = JSON.parse(this.selectedSheet);
          this.selectedSheet = parsed;
          // Reset classification when sheet changes
          this.sheetClassification = null;
          this.sheetColumns = [];
        } catch {
          this.errorMessage = 'Could not read the selected sheet — pick it again.';
          this.selectedSheet = null;
        }
      }
    },

    // Classify sheet to extract columns
    async classifySheet() {
      if (!this.selectedSheet) return;

      this.isClassifying = true;
      this.clearError();

      try {
        const sheetsToClassify = [{
          file_name: this.selectedSheet.fileItem.file_name,
          sheet_name: this.selectedSheet.sheet.name,
          s3_key: this.selectedSheet.fileItem.s3_key || this.fileAnalysis.original_file,
          from_zip: this.selectedSheet.fileItem.from_zip || false,
          zip_s3_key: this.selectedSheet.fileItem.from_zip ? this.fileAnalysis.original_zip : null
        }];

        // classify-sheets is asynchronous (202 + job) — poll the job.
        const { data: job } = await axios.post('/api/etl_module/classify-sheets/', {
          sheets: sheetsToClassify
        });
        const classifications = await this.pollClassificationJob(job.job_id);

        if (classifications && classifications.length > 0) {
          this.sheetClassification = classifications[0];
          this.sheetColumns = this.sheetClassification.columns.map(col => col.name);
        } else {
          this.errorMessage = 'No classification results returned';
        }

      } catch (error) {
        this.errorMessage = `Classification failed: ${error.response?.data?.error || error.message}`;
      } finally {
        this.isClassifying = false;
      }
    },

    async pollClassificationJob(jobId) {
      const started = Date.now();
      this.pollCancelled = false;
      while (!this.pollCancelled) {
        const { data } = await axios.get(`/api/jobs/${jobId}/status/`);
        if (data.status === 'completed') return data.result_data.classifications;
        if (data.status === 'failed') throw new Error(data.error_message || 'Classification failed');
        if (Date.now() - started > 5 * 60 * 1000) throw new Error('Classification timed out');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      return [];
    },

    // Model management. These four throw on failure — mounted() collects the
    // failures into one message instead of each overwriting the last.
    async loadAvailableModels() {
      const response = await axios.get('/api/etl_module/model-import/models/');
      this.availableModels = response.data.models || [];
    },

    async loadIdentifierTypes() {
      const response = await axios.get('/api/etl_module/model-import/identifier-types/');
      this.identifierTypes = response.data.identifier_types;
    },

    async loadInfoTagTypes() {
      const response = await axios.get('/api/etl_module/model-import/infotag-types/');
      this.infotagTypes = response.data.infotag_types;
    },

    async loadEntityTypes() {
      const response = await axios.get('/api/etl_module/model-import/entity-types/');
      this.entityTypes = response.data.entity_types;
    },

    async loadEntityAttributes() {
      if (!this.entityConfig.entity_type_id) {
        this.entityAttributes = [];
        return;
      }

      try {
        const response = await axios.get('/api/etl_module/model-import/entity-attributes/', {
          params: {
            entity_type_id: this.entityConfig.entity_type_id
          }
        });
        this.entityAttributes = response.data.attributes;
        // Reset attribute mappings when entity type changes
        this.entityConfig.attribute_mappings = {};
      } catch (error) {
        this.errorMessage = `Failed to load EntityAttributes: ${error.response?.data?.error || error.message}`;
      }
    },

    async onModelSelected() {
      if (!this.selectedModel) return;

      try {
        const response = await axios.get('/api/etl_module/model-import/model-fields/', {
          params: {
            app: this.selectedModel.app,
            model: this.selectedModel.model
          }
        });

        this.modelFields = response.data.fields;

        // Initialize mappings
        this.fieldMappings = {};
        this.foreignKeyMappings = {};

        // Sheet columns should already be populated from classification
        if (!this.sheetColumns || this.sheetColumns.length === 0) {
          if (this.sheetClassification && this.sheetClassification.columns) {
            this.sheetColumns = this.sheetClassification.columns.map(col => col.name);
          }
        }

        // Initialize FK mappings with empty objects based on related model type
        this.foreignKeyFields.forEach(field => {
          if (field.related_model === 'Entity') {
            // Entity uses InfoTag lookup
            this.foreignKeyMappings[field.name] = {
              related_model: field.related_model,
              lookup_type: 'entity_identifier',
              sheet_column: null,
              identifier_type: null,
              use_fuzzy_matching: false,
              fuzzy_threshold: 80
            };
          } else {
            // Other models use direct field match
            this.foreignKeyMappings[field.name] = {
              related_model: field.related_model,
              lookup_type: 'direct_field_match',
              sheet_column: null,
              model_field: null,
              use_fuzzy_matching: false,
              fuzzy_threshold: 80
            };
          }
        });

      } catch (error) {
        this.errorMessage = `Failed to load model fields: ${error.response?.data?.error || error.message}`;
      }
    },

    // Load fields for related models (for FK mapping)
    async loadRelatedModelFields(relatedModel, fieldName) {
      // Check cache first
      if (this.relatedModelFieldsCache[fieldName]) {
        return;
      }

      try {
        const response = await axios.get('/api/etl_module/model-import/model-fields/', {
          params: {
            app: 'api',
            model: relatedModel
          }
        });

        // Cache the fields
        this.relatedModelFieldsCache[fieldName] = response.data.fields.filter(f => !f.system_field);

      } catch (error) {
        this.errorMessage = `Failed to load ${relatedModel} fields: ${error.response?.data?.error || error.message}`;
      }
    },

    // Preview & validation
    async previewImport() {
      this.isPreviewing = true;
      this.clearError();
      this.previewResults = null;

      try {
        const importConfig = this.buildImportConfig();

        const response = await axios.post('/api/etl_module/model-import/preview/', {
          file_info: this.buildFileInfo(),
          sheet_info: this.buildSheetInfo(),
          import_config: importConfig
        });

        this.previewResults = response.data;

      } catch (error) {
        this.errorMessage = `Preview failed: ${error.response?.data?.error || error.message}`;
      } finally {
        this.isPreviewing = false;
      }
    },

    // Execution — two-click arm/confirm: this writes real rows.
    onExecuteClick() {
      if (!this.executeArmed) {
        this.executeArmed = true;
        setTimeout(() => { this.executeArmed = false; }, 5000);
        return;
      }
      this.executeArmed = false;
      this.executeImport(this.showSkipInvalidOption);
    },

    async executeImport(skipInvalidRows = false) {
      this.isExecuting = true;
      this.clearError();

      try {
        const importConfig = this.buildImportConfig();

        const response = await axios.post('/api/etl_module/model-import/execute/', {
          file_info: this.buildFileInfo(),
          sheet_info: this.buildSheetInfo(),
          import_config: importConfig,
          skip_invalid_rows: skipInvalidRows
        });

        this.executionResults = response.data;

      } catch (error) {
        this.errorMessage = `Import failed: ${error.response?.data?.error || error.message}`;
      } finally {
        this.isExecuting = false;
      }
    },

    // Helper methods
    buildFileInfo() {
      return {
        s3_key: this.activeFile.s3_key,
        filename: this.selectedSheet.fileItem.file_name,
        from_zip: this.selectedSheet.fileItem.from_zip || false,
        zip_s3_key: this.fileAnalysis.original_zip
      };
    },

    buildSheetInfo() {
      return {
        sheet_name: this.selectedSheet.sheet.name,
        boundaries: {
          header_row: 0,
          data_start_row: 1
          // Don't specify start_col/end_col - read all columns from the sheet
        }
      };
    },

    buildImportConfig() {
      const config = {
        target_app: this.selectedModel.app,
        target_model: this.selectedModel.model
      };

      if (this.isEntityImport) {
        // Entity-specific configuration
        config.entity_type_id = this.entityConfig.entity_type_id;
        config.entity_name_column = this.entityConfig.entity_name_column;
        config.entity_identifier_column = this.entityConfig.entity_identifier_column;
        config.entity_identifier_type = this.entityConfig.entity_identifier_type;
        config.parent_identifier_column = this.entityConfig.parent_identifier_column;
        config.parent_identifier_type = this.entityConfig.parent_identifier_type;
        config.use_parent_fuzzy_matching = this.entityConfig.use_parent_fuzzy_matching;
        config.parent_fuzzy_threshold = this.entityConfig.parent_fuzzy_threshold;
        config.attribute_mappings = this.entityConfig.attribute_mappings;
      } else if (this.isInfoTagImport) {
        // InfoTag-specific configuration
        config.infotag_type_id = this.infotagConfig.infotag_type_id;
        config.infotag_name_column = this.infotagConfig.infotag_name_column;
        config.entity_identifier_column = this.infotagConfig.entity_identifier_column;
        config.entity_identifier_type = this.infotagConfig.entity_identifier_type;
        config.use_entity_fuzzy_matching = this.infotagConfig.use_entity_fuzzy_matching;
        config.entity_fuzzy_threshold = this.infotagConfig.entity_fuzzy_threshold;
        config.use_fuzzy_matching = this.infotagConfig.use_fuzzy_matching;
        config.fuzzy_threshold = this.infotagConfig.fuzzy_threshold;
        config.overwrite = this.infotagConfig.overwrite;
      } else {
        // Regular model import configuration.
        // '' counts as unmapped (see canPreview) — filter it out too.
        const isMapped = (v) => v !== null && v !== undefined && v !== '';
        const cleanFieldMappings = {};
        Object.keys(this.fieldMappings).forEach(key => {
          if (isMapped(this.fieldMappings[key])) {
            cleanFieldMappings[key] = this.fieldMappings[key];
          }
        });

        const cleanFKMappings = {};
        Object.keys(this.foreignKeyMappings).forEach(key => {
          const fk = this.foreignKeyMappings[key];

          // Validate based on lookup type
          if (fk.lookup_type === 'entity_identifier') {
            if (isMapped(fk.sheet_column) && isMapped(fk.identifier_type)) {
              cleanFKMappings[key] = fk;
            }
          } else if (fk.lookup_type === 'direct_field_match') {
            if (isMapped(fk.sheet_column) && isMapped(fk.model_field)) {
              cleanFKMappings[key] = fk;
            }
          }
        });

        config.field_mappings = cleanFieldMappings;
        config.foreign_key_mappings = cleanFKMappings;
      }

      return config;
    },

    getTotalUnresolvedFKs() {
      if (!this.previewResults?.validation_results?.foreign_key_validation) return 0;
      return Object.values(this.previewResults.validation_results.foreign_key_validation)
        .reduce((sum, fk) => sum + (fk.not_found || 0), 0);
    },

    resetWorkflow() {
      // Reset EVERYTHING — the old version kept the previous run's file,
      // Entity/InfoTag config (identifier columns, thresholds, the overwrite
      // flag) and carried them invisibly into the next import.
      this.localFile = null;
      this.activeFile = null;
      this.fileAnalysis = null;
      this.selectedSheet = null;
      this.sheetClassification = null;
      this.sheetColumns = [];
      this.selectedModel = null;
      this.modelFields = null;
      this.fieldMappings = {};
      this.foreignKeyMappings = {};
      this.relatedModelFieldsCache = {};
      this.entityAttributes = [];
      this.entityConfig = {
        entity_type_id: null,
        entity_name_column: null,
        entity_identifier_column: null,
        entity_identifier_type: null,
        parent_identifier_column: null,
        parent_identifier_type: null,
        use_parent_fuzzy_matching: false,
        parent_fuzzy_threshold: 80,
        attribute_mappings: {}
      };
      this.infotagConfig = {
        infotag_type_id: null,
        infotag_name_column: null,
        entity_identifier_column: null,
        entity_identifier_type: null,
        use_entity_fuzzy_matching: false,
        entity_fuzzy_threshold: 80,
        use_fuzzy_matching: false,
        fuzzy_threshold: 80,
        overwrite: false
      };
      this.previewResults = null;
      this.executionResults = null;
      this.showSkipInvalidOption = false;
      this.errorMessage = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    clearError() {
      this.errorMessage = null;
    },

    getFKValidationClass(fkVal) {
      // If there are fuzzy matches, use purple color scheme
      if (fkVal.fuzzy_match_count > 0) {
        return 'bg-purple-50 border-purple-200';
      }
      // Otherwise use green for success, red for errors
      return fkVal.not_found === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
    },

    getScoreClass(score) {
      // Color-code match scores
      if (score >= 95) {
        return 'bg-green-100 text-green-800';
      } else if (score >= 85) {
        return 'bg-blue-100 text-blue-800';
      } else if (score >= 75) {
        return 'bg-yellow-100 text-yellow-800';
      } else {
        return 'bg-orange-100 text-orange-800';
      }
    },
  },
};
</script>

<style scoped>
</style>
